import { useTheme } from "@/components/ThemeProvider";
import { THEME_OPTIONS } from "@/lib/themes";
import { cn } from "@/lib/utils/cn";
import { Check, Moon, Sparkles, Sun, type LucideIcon } from "lucide-react";

const themeIcons: Record<(typeof THEME_OPTIONS)[number]["id"], LucideIcon> = {
  light: Sun,
  dark: Moon,
  midnight: Sparkles,
};

export function ThemeSelector() {
  const { setTheme, theme } = useTheme();

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      {THEME_OPTIONS.map((option) => {
        const Icon = themeIcons[option.id];
        const isActive = theme === option.id;

        return (
          <button
            key={option.id}
            type="button"
            aria-pressed={isActive}
            className={cn(
              "border-border bg-card text-card-foreground hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring focus-visible:ring-offset-background relative flex min-h-32 flex-col rounded-xl border p-4 text-left shadow-xs transition-[background-color,border-color,box-shadow,transform] focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
              isActive
                ? "border-ring ring-ring/25 ring-2"
                : "hover:-translate-y-0.5 hover:shadow-md",
            )}
            onClick={() => {
              setTheme(option.id);
            }}
          >
            <div className="flex w-full items-start justify-between gap-3">
              <span className="bg-secondary text-secondary-foreground flex h-8 w-8 items-center justify-center rounded-lg">
                <Icon className="h-4 w-4" />
              </span>
              {isActive ? (
                <span className="bg-primary text-primary-foreground flex h-6 w-6 items-center justify-center rounded-full">
                  <Check className="h-3.5 w-3.5" />
                  <span className="sr-only">Selected</span>
                </span>
              ) : null}
            </div>
            <span className="mt-3 text-sm font-semibold">{option.label}</span>
            <span className="text-muted-foreground mt-1 text-xs leading-5">
              {option.description}
            </span>
            <span className="mt-auto flex gap-1.5 pt-3" aria-hidden="true">
              {option.previewColors.map((color) => (
                <span
                  key={color}
                  className="border-border h-3.5 w-8 rounded-full border shadow-xs"
                  style={{ backgroundColor: color }}
                />
              ))}
            </span>
          </button>
        );
      })}
    </div>
  );
}
